/**
 * DependencyGraph - Visualizes view dependencies as an interactive tree
 *
 * Shows upstream dependencies (views this view depends on) and/or
 * downstream dependents (views that depend on this view)
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowRight, Database } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useViewDependencies, useViewDependents } from '@/hooks/useViews';
import { DependencyHelp } from './HelpTooltips';
import type { ViewDependencyInfo } from '@/types/views';

export interface DependencyGraphProps {
  viewId: string;
  viewName?: string;
  direction: 'upstream' | 'downstream' | 'both';
  onViewClick?: (viewId: string) => void;
}

/**
 * DependencyGraph Component
 *
 * Visualizes view dependencies as an interactive graph.
 * - upstream: shows views this view depends on
 * - downstream: shows views that depend on this view
 * - both: shows full dependency tree
 */
export const DependencyGraph: React.FC<DependencyGraphProps> = ({
  viewId,
  viewName,
  direction,
  onViewClick,
}) => {
  const navigate = useNavigate();

  // Fetch dependencies and dependents
  const {
    data: dependenciesData,
    isLoading: isLoadingDeps,
    error: depsError,
  } = useViewDependencies(viewId, {
    enabled: direction === 'upstream' || direction === 'both',
  });

  const {
    data: dependentsData,
    isLoading: isLoadingDependents,
    error: dependentsError,
  } = useViewDependents(viewId, {
    enabled: direction === 'downstream' || direction === 'both',
  });

  const handleViewClick = (clickedViewId: string) => {
    if (onViewClick) {
      onViewClick(clickedViewId);
    } else {
      navigate(`/views/${clickedViewId}`);
    }
  };

  // Loading state - use skeleton loaders instead of spinner
  const isLoading = isLoadingDeps || isLoadingDependents;
  if (isLoading) {
    return (
      <div className="space-y-6">
        {(direction === 'upstream' || direction === 'both') && (
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-4 w-64 mt-2" />
            </CardHeader>
            <CardContent className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border">
                  <Skeleton className="h-10 w-10 rounded" />
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-10 w-10 rounded" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
        {(direction === 'downstream' || direction === 'both') && (
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-4 w-64 mt-2" />
            </CardHeader>
            <CardContent className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border">
                  <Skeleton className="h-10 w-10 rounded" />
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-10 w-10 rounded" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Error state
  const error = depsError || dependentsError;
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load dependency information: {error.message}
        </AlertDescription>
      </Alert>
    );
  }

  const dependencies = dependenciesData?.dependencies || [];
  const dependents = dependentsData?.dependents || [];
  const hasUpstream = direction !== 'downstream' && dependencies.length > 0;
  const hasDownstream = direction !== 'upstream' && dependents.length > 0;
  const hasAny = hasUpstream || hasDownstream;

  // Empty state
  if (!hasAny) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-muted-foreground">
            <Database className="h-12 w-12 mx-auto mb-4 opacity-20" />
            <p className="text-sm">
              {direction === 'upstream' && 'This view does not depend on any other views'}
              {direction === 'downstream' && 'No views depend on this view'}
              {direction === 'both' && 'This view has no dependencies or dependents'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upstream Dependencies */}
      {hasUpstream && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              Upstream Dependencies
              <DependencyHelp />
            </CardTitle>
            <CardDescription>
              Views that this view depends on ({dependencies.length})
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DependencyList
              items={dependencies}
              currentViewId={viewId}
              currentViewName={viewName}
              type="upstream"
              onViewClick={handleViewClick}
            />
          </CardContent>
        </Card>
      )}

      {/* Downstream Dependents */}
      {hasDownstream && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Downstream Dependents</CardTitle>
            <CardDescription>
              Views that depend on this view ({dependents.length})
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DependencyList
              items={dependents}
              currentViewId={viewId}
              currentViewName={viewName}
              type="downstream"
              onViewClick={handleViewClick}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/**
 * DependencyList - Renders a list of dependency items
 */
interface DependencyListProps {
  items: ViewDependencyInfo[];
  currentViewId: string;
  currentViewName?: string;
  type: 'upstream' | 'downstream';
  onViewClick: (viewId: string) => void;
}

const DependencyList: React.FC<DependencyListProps> = ({
  items,
  currentViewId,
  currentViewName,
  type,
  onViewClick,
}) => {
  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <DependencyItem
          key={item.view_id}
          item={item}
          currentViewId={currentViewId}
          currentViewName={currentViewName}
          type={type}
          isLast={index === items.length - 1}
          onViewClick={onViewClick}
        />
      ))}
    </div>
  );
};

/**
 * DependencyItem - Renders a single dependency item
 */
interface DependencyItemProps {
  item: ViewDependencyInfo;
  currentViewId?: string;
  currentViewName?: string;
  type: 'upstream' | 'downstream';
  isLast?: boolean;
  onViewClick: (viewId: string) => void;
}

const DependencyItem: React.FC<DependencyItemProps> = ({
  item,
  currentViewName,
  type,
  onViewClick,
}) => {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
      {/* Visual connector */}
      <div className="flex items-center gap-2">
        {type === 'upstream' ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="h-auto p-1.5 hover:bg-primary/10"
              onClick={() => onViewClick(item.view_id)}
            >
              <Database className="h-4 w-4 text-primary" />
            </Button>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">{currentViewName || 'Current View'}</span>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">{currentViewName || 'Current View'}</span>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <Button
              variant="ghost"
              size="sm"
              className="h-auto p-1.5 hover:bg-primary/10"
              onClick={() => onViewClick(item.view_id)}
            >
              <Database className="h-4 w-4 text-alert" />
            </Button>
          </>
        )}
      </div>

      {/* View info */}
      <div className="flex-1 min-w-0">
        <Button
          variant="link"
          className="h-auto p-0 font-medium text-foreground hover:text-primary"
          onClick={() => onViewClick(item.view_id)}
        >
          {item.view_name}
        </Button>
        {item.join_type && (
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="secondary" className="text-xs">
              {item.join_type.toUpperCase()} JOIN
            </Badge>
          </div>
        )}
      </div>
    </div>
  );
};
